import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import DateInput from '../common/DateInput';
import { BlockUI } from 'primereact/blockui';
import DriverFilters, { useDriverList } from '../common/DriverFilters';
import ActivityDetailsGrid from '../common/ActivityDetailsGrid';
import TrakaDana from '../common/TrakaDana';
import { zakljucan } from '../common/IzvorAktivnosti';
import PageHeader, { EmptyState, tableEmpty } from '../common/PageHeader';
import TableSearch, { filtriraj, usePage, PAGINATOR } from '../common/TableSearch';
import { driversApi, specificConditionApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { DAY_MS, fmtDate, fmtDuration, fmtTime, sameDay, startOfToday, toDateStr } from '../../utils/dates';
import { OUT_TYPES, normalizeDetail, shiftDetails, workDayBreak, sortDetails } from '../../utils/activities';

export const TOTAL_KEYS = ['totalBreakTime', 'totalDrivingTime', 'totalNightDrivingTime', 'totalWorkTime', 'totalNightWorkTime',
    'totalAvailabilityTime', 'totalNightAvailabilityTime', 'totalUnknownTime'];
const pickTotals = (t) => Object.fromEntries(TOTAL_KEYS.map((k) => [k, t[k] || 0]));

/** naslov kolone sa tačkom boje aktivnosti (iste boje kao traka i Bulk listići) */
const NaslovAkt = ({ tip, tekst }) => <span className="naslov-akt"><i className={`act-dot ${tip}`} aria-hidden="true" />{tekst}</span>;

const dur = (ms) => (ms === DAY_MS ? '24:00' : fmtDuration(ms));
const DANA_PO_STRANI = 10; // ispod tabele su detalji dana, pa tabela ima stalnu visinu

export default function Activities({ active }) {
    const alerts = useAlerts();
    const list = useDriverList([]);
    const { driver } = list;
    const [from, setFrom] = useState(startOfToday());
    const [to, setTo] = useState(startOfToday());
    const [days, setDays] = useState([]);
    const [toDelete, setToDelete] = useState([]);
    const [search, setSearch] = useState('');
    const [outs, setOuts] = useState([]);
    const [selKey, setSelKey] = useState(null);
    const [busy, setBusy] = useState(false);
    const [canSave, setCanSave] = useState(false);
    const [editKey, setEditKey] = useState(null);
    const [editRow, setEditRow] = useState(null);

    const keyOf = (d) => d._key || d.id;
    const selected = days.find((d) => keyOf(d) === selKey);

    const load = async (d = driver) => {
        if (!d) return;
        setBusy(true);
        try {
            const [acts, sc] = await Promise.all([
                driversApi.activities(d.id, from, to),
                specificConditionApi.get(d.id, from, to),
            ]);
            const rows = acts.map((a) => ({ ...a, _key: `d${a.id}` }));
            setDays(rows);
            setOuts(sc);
            setToDelete([]);
            setSelKey(rows[0] ? rows[0]._key : null);
            setCanSave(true);
        } catch { setCanSave(false); }
        setBusy(false);
    };

    /* Kao stari klijent: aktivnosti se učitavaju pri otvaranju (prvi vozač) i na Osveži; promena vozača ili
       perioda samo poništava mogućnost snimanja dok se ponovo ne učita. */
    const initialLoaded = useRef(false);
    useEffect(() => { setCanSave(false); }, [driver && driver.id, from, to]);
    useEffect(() => {
        if (driver && !initialLoaded.current) { initialLoaded.current = true; load(driver); }
    }, [driver && driver.id]);
    const onDriversRefreshed = (all) => load(all.find((x) => driver && x.id === driver.id) || all[0]);

    const hasOut = (day) => outs.some((o) => sameDay(o.entryDate, day.activityDate));
    const dayOuts = useMemo(
        () => (selected ? outs.filter((o) => sameDay(o.entryDate, selected.activityDate)).sort((a, b) => a.entryDate - b.entryDate) : []),
        [outs, selected]
    );

    /* klik na izvor dana (Traka 1 / Ručno...) iznad tabele bira sve njegove redove za brisanje */
    const [izborIzvora, setIzborIzvora] = useState(null);

    const removeDay = async (d) => {
        /* dan sa aktivnostima sa kartice se ne briše (baza bi odbila snimanje) */
        if ((d.activityDetails || []).some(zakljucan)) {
            alerts.error(`Dan ${fmtDate(d.activityDate)} ima aktivnosti sa kartice, koje se ne brišu.`);
            return;
        }
        if (!(await alerts.confirm('Potvrdi', `Da li ste sigurni da želite da obrišete sve aktivnosti za '${fmtDate(d.activityDate)}'?`, { severity: 'danger' }))) return;
        /* kao stari DataGrid: posle brisanja izabranog reda bira se susedni */
        const idx = days.findIndex((r) => keyOf(r) === keyOf(d));
        const rest = days.filter((r) => keyOf(r) !== keyOf(d));
        setDays(rest);
        if (d.id) setToDelete((x) => [...x, d]);
        if (selKey === keyOf(d)) {
            const next = rest[Math.min(idx, rest.length - 1)];
            setSelKey(next ? keyOf(next) : null);
        }
    };

    const recalc = async (day, details) => {
        const normalized = sortDetails(details.map((d) => normalizeDetail(d)));
        const draft = { ...day, activityDetails: normalized };
        setDays((x) => x.map((r) => (keyOf(r) === keyOf(day) ? draft : r)));
        try {
            const t = await driversApi.totals(draft);
            /* server vraća samo ukupne vrednosti; detalji ostaju kako ih je korisnik uneo */
            setDays((x) => x.map((r) => (keyOf(r) === keyOf(day) ? { ...draft, ...pickTotals(t) } : r)));
        } catch { /* toast */ }
    };

    const saveAll = async () => {
        if (!(await alerts.confirm('Potvrdi', 'Da li ste sigurni da želite sačuvate SVE aktivnosti?'))) return;
        setBusy(true);
        try {
            const res = await driversApi.saveActivities({ driverId: driver.id, toUpdate: days, toDelete });
            const rows = res.map((a) => ({ ...a, _key: `d${a.id}` }));
            setDays(rows);
            setToDelete([]);
            setSelKey(rows[0] ? rows[0]._key : null);
            alerts.success('Aktivnosti su uspešno ažurirane');
        } catch { alerts.error('Ažuriranje aktivnosti neuspešno'); }
        setBusy(false);
    };

    /* olovka: ispravka DATUMA dana (dan unet pod pogrešnim datumom) u samom redu; potvrda odmah čuva (detalji se pomeraju na novi datum) */
    const startEdit = (d) => {
        setEditKey(keyOf(d));
        setEditRow({ activityDate: d.activityDate });
    };
    const commitEdit = async () => {
        const stari = days.find((r) => keyOf(r) === editKey);
        if (!stari || !editRow.activityDate || sameDay(stari.activityDate, editRow.activityDate)) { setEditKey(null); return; }
        if (!(await alerts.confirm('Potvrdi', `Premestiti aktivnosti sa ${fmtDate(stari.activityDate)} na ${fmtDate(editRow.activityDate)} i sačuvati?`))) return;
        const novi = days.map((r) => (keyOf(r) !== editKey ? r : {
            ...r, activityDate: editRow.activityDate,
            activityDetails: shiftDetails(r.activityDetails || [], r.activityDate, editRow.activityDate),
        }));
        setEditKey(null);
        setBusy(true);
        try {
            const res = await driversApi.saveActivities({ driverId: driver.id, toUpdate: novi, toDelete });
            const rows = res.map((x) => ({ ...x, _key: `d${x.id}` }));
            setDays(rows);
            setToDelete([]);
            setSelKey((rows.find((x) => sameDay(x.activityDate, editRow.activityDate)) || rows[0] || {})._key || null);
            alerts.success('Datum je promenjen i sačuvan');
        } catch { /* poruka servera (npr. dan već postoji) je prikazana; stanje ostaje kao pre izmene */ }
        setBusy(false);
    };
    const editing = (d) => editKey === keyOf(d);

    /* pretraga dana: datum, distanca, poruka, "OUT" */
    const filtered = useMemo(() => filtriraj(days, search, (d) => [fmtDate(d.activityDate), d.traversedDistance, d.note, hasOut(d) ? 'OUT' : '']), [days, search, outs]); // eslint-disable-line react-hooks/exhaustive-deps
    /* DataTable ne iscrtava ponovo red čiji se podaci nisu promenili — red u izmeni dobija nov objekat (ranije olovka "nije radila ništa") */
    const prikaz = useMemo(() => (editKey ? filtered.map((r) => (keyOf(r) === editKey ? { ...r, _izmena: editRow } : r)) : filtered), [filtered, editKey, editRow]); // eslint-disable-line react-hooks/exhaustive-deps
    const [first, setFirst] = usePage(filtered.length, DANA_PO_STRANI, search);

    const numCol = { headerClassName: 'num', bodyClassName: 'num', style: { minWidth: '7rem' } };

    return (
        <BlockUI blocked={busy} template={<i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem' }} aria-label="Učitavanje" />}>
            <PageHeader title="Pregled aktivnosti" subtitle="Aktivnosti vozača po danima za izabrani period, sa izmenom i brisanjem.">
                <Button label="Sačuvaj" icon="pi pi-check" onClick={saveAll} disabled={!canSave || busy || (!days.length && !toDelete.length)} />
            </PageHeader>

            <div className="panel">
                <DriverFilters list={list} required onRefreshed={onDriversRefreshed}>
                    <div className="field date"><label htmlFor="act-from">Od</label>
                        <DateInput inputId="act-from" value={from} onChange={(e) => setFrom(e.value)} dateFormat="dd.mm.yy" showIcon monthNavigator yearNavigator yearRange="2010:2040" /></div>
                    <div className="field date"><label htmlFor="act-to">Do</label>
                        <DateInput inputId="act-to" value={to} onChange={(e) => setTo(e.value)} dateFormat="dd.mm.yy" showIcon monthNavigator yearNavigator yearRange="2010:2040" /></div>
                    <div className="filters-actions">
                        <Button label="Prikaži" icon="pi pi-refresh" severity="secondary" outlined tooltip="Osveži aktivnosti" tooltipOptions={{ position: 'bottom' }}
                            onClick={() => load()} disabled={!driver || busy} />
                    </div>
                </DriverFilters>
                {!canSave && driver && days.length > 0 && (
                    <p className="panel-note"><i className="pi pi-info-circle" aria-hidden="true" />Vozač ili period su promenjeni. Učitajte aktivnosti ponovo pre čuvanja.</p>
                )}
            </div>

            <section className="panel" aria-labelledby="act-days">
                <div className="panel-head">
                    <div>
                        <h3 className="panel-title" id="act-days">Aktivnosti po danu</h3>
                        <p className="panel-sub">Klik na red prikazuje detalje dana; olovka ispravlja datum dana (potvrda odmah čuva).</p>
                    </div>
                </div>
                <TableSearch value={search} onChange={setSearch} placeholder="Pretraga (datum, distanca, poruka, OUT...)" label="Pretraga dana"
                    ukupno={days.length} pronadjeno={filtered.length} jedinica={days.length === 1 ? 'dan' : 'dana'} />
                <DataTable value={prikaz} dataKey="_key" size="small" scrollable {...PAGINATOR} rows={DANA_PO_STRANI} first={first} onPage={(e) => setFirst(e.first)}
                    emptyMessage={tableEmpty(search.trim() && days.length ? 'Nijedan dan ne odgovara pretrazi' : driver ? 'Nijedna aktivnost nije pronađena za izabrani period' : 'Izaberite vozača', 'pi pi-calendar')}
                    onRowClick={(e) => setSelKey(keyOf(e.data))} rowClassName={(r) => `row-clickable${keyOf(r) === selKey ? ' row-selected' : ''}`}
                    sortField="activityDate" sortOrder={1}>
                    <Column header="" headerClassName="actions" bodyClassName="actions" style={{ width: '6rem' }} body={(d) => (editing(d) ? (
                        <span>
                            <Button icon="pi pi-check" rounded text severity="success" onClick={commitEdit} aria-label="Potvrdi i sačuvaj datum" tooltip="Potvrdi i sačuvaj" />
                            <Button icon="pi pi-times" rounded text severity="secondary" onClick={() => setEditKey(null)} aria-label="Otkaži izmenu" tooltip="Otkaži" />
                        </span>
                    ) : (
                        <span>
                            <Button icon="pi pi-pencil" rounded text severity="secondary" onClick={(e) => { e.stopPropagation(); startEdit(d); }} aria-label="Promeni datum dana" tooltip="Promeni datum" />
                            <Button icon="pi pi-trash" rounded text severity="danger" onClick={(e) => { e.stopPropagation(); removeDay(d); }} aria-label="Obriši dan" tooltip="Obriši" />
                        </span>
                    ))} />
                    <Column header="Datum" field="activityDate" sortable style={{ minWidth: '9.5rem' }} body={(d) => (editing(d)
                        ? <DateInput value={editRow.activityDate} onChange={(e) => setEditRow({ ...editRow, activityDate: e.value })} dateFormat="dd.mm.yy" appendTo={document.body} />
                        : <b>{fmtDate(d.activityDate)}</b>)} />
                    <Column header="Pređena distanca" {...numCol} body={(d) => d.traversedDistance} />
                    <Column header="OUT stanje" style={{ minWidth: '6.5rem' }} body={(d) => (hasOut(d) ? <span className="pill warn">Ima OUT</span> : '')} />
                    <Column header={<NaslovAkt tip="driving" tekst="Vožnja - ukupno" />} {...numCol} body={(d) => dur(d.totalDrivingTime)} />
                    <Column header={<NaslovAkt tip="driving" tekst="Noćna vožnja - ukupno" />} {...numCol} body={(d) => dur(d.totalNightDrivingTime)} />
                    <Column header={<NaslovAkt tip="work" tekst="Ostali rad - ukupno" />} {...numCol} body={(d) => dur(d.totalWorkTime)} />
                    <Column header={<NaslovAkt tip="work" tekst="Noćni ostali rad - ukupno" />} {...numCol} body={(d) => dur(d.totalNightWorkTime)} />
                    <Column header={<NaslovAkt tip="availability" tekst="Raspoloživost - ukupno" />} {...numCol} body={(d) => dur(d.totalAvailabilityTime)} />
                    <Column header={<NaslovAkt tip="rest" tekst="Odmor u toku radnog vremena" />} {...numCol} body={(d) => dur(workDayBreak(d.activityDetails))} />
                    <Column header={<NaslovAkt tip="rest" tekst="Odmor u toku dana" />} {...numCol} body={(d) => dur(d.totalBreakTime)} />
                    <Column header="Nepoznato vreme - ukupno" {...numCol} body={(d) => dur(d.totalUnknownTime)} />
                    <Column header="Poruka" style={{ minWidth: '12rem' }} body={(d) => d.note} />
                </DataTable>
            </section>

            <div className="layout-split wide-side">
                <section className="panel" aria-labelledby="act-details">
                    <div className="panel-head">
                        <div>
                            <h3 className="panel-title" id="act-details">Aktivnosti - detalji</h3>
                            {selected && <p className="panel-sub">{fmtDate(selected.activityDate)}</p>}
                        </div>
                    </div>
                    {selected ? (
                        <>
                        <TrakaDana details={selected.activityDetails} day={selected.activityDate} naslov={fmtDate(selected.activityDate)} izvor
                            onIzvor={(g) => g && setIzborIzvora({ kljuc: g.kljuc, n: Date.now() })} />
                        <div className="grid-box">
                            <ActivityDetailsGrid details={selected.activityDetails || []} day={selected.activityDate} confirmDelete visestruko prikaziIzvor izaberi={izborIzvora}
                                onChange={(l) => recalc(selected, l)} />
                        </div>
                        </>
                    ) : <EmptyState compact icon="pi pi-list" title="Nije izabran dan" text="Izaberite red u tabeli iznad da biste videli i menjali detalje." />}
                </section>
                <section className="panel" aria-labelledby="act-out">
                    <div className="panel-head"><h3 className="panel-title" id="act-out">OUT - detalji</h3></div>
                    <DataTable value={dayOuts} dataKey="id" size="small" emptyMessage={tableEmpty('Nema OUT zapisa za izabrani dan', 'pi pi-flag')}>
                        <Column header="Tip" body={(o) => OUT_TYPES[o.specificConditionType]} />
                        <Column header="Datum" body={(o) => fmtTime(o.entryDate)} />
                    </DataTable>
                </section>
            </div>
        </BlockUI>
    );
}
